import type { TemplateRef } from '@angular/core';
import { Component, computed, effect, viewChild } from '@angular/core';
import { ChangeDetectionStrategy, inject } from '@angular/core';

import { SessionTimeoutService } from '#/app/core/session/session-timeout.service';
import type { ConfirmationDialogOptions } from '#/app/shared/domain/ui/confirmation-dialog.model';
import { AuthenticationService } from '#/app/shared/services/auth/authentication.service';
import { DialogService } from '#/app/shared/services/dialog.service';

const SESSION_TIMEOUT_WARNING_DIALOG_ID = 'session-timeout-warning-dialog';

@Component({
  selector: 'app-session-timeout',
  imports: [],
  templateUrl: './session-timeout.html',
  styleUrl: './session-timeout.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SessionTimeoutComponent {
  readonly timeoutWarningContent = viewChild<TemplateRef<unknown>>(
    'timeoutWarningContent',
  );

  readonly #authenticationService = inject(AuthenticationService);
  readonly #sessionTimeoutService = inject(SessionTimeoutService);
  readonly #dialogService = inject(DialogService);

  protected readonly formattedSecondsRemaining = computed(() => {
    const seconds = this.#sessionTimeoutService.secondsRemaining();
    return this.#formatSeconds(seconds);
  });

  constructor() {
    effect(() => {
      const isWarningVisible = this.#sessionTimeoutService.isWarningVisible();
      const isAuthenticated = this.#authenticationService.isAuthenticated();
      if (isAuthenticated && isWarningVisible) {
        if (this.#dialogService.isDialogOpen(SESSION_TIMEOUT_WARNING_DIALOG_ID))
          return;
        this.#openWarningDialog();
      } else {
        this.#dialogService.close(SESSION_TIMEOUT_WARNING_DIALOG_ID);
      }
    });
  }

  #formatSeconds(seconds: number): string {
    return new Date(seconds * 1000).toISOString().substring(14, 19);
  }

  #openWarningDialog(): void {
    const dialogOptions: ConfirmationDialogOptions = {
      header: 'Session Timeout',
      message: '',
      messageTemplate: this.timeoutWarningContent(),
      rejectButtonOptions: {
        label: 'Log out',
      },
      acceptButtonOptions: {
        label: 'Continue Session',
      },
      reject: () => {
        this.#authenticationService.logout();
      },
      accept: () => {
        this.#sessionTimeoutService.continueSession();
        this.#sessionTimeoutService.broadcastActivityToOtherTabs();
      },
    };

    this.#dialogService.confirm(
      dialogOptions,
      SESSION_TIMEOUT_WARNING_DIALOG_ID,
    );
  }
}
